import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocalStorage } from "react-use";

import { User } from "pwapi";

import { fetchCases } from "shared/actions/casesActions";
import {
  CaseListDirection,
  QueueEnum,
  userDefaultQueue,
} from "shared/reducers/casesReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";

import HiddenDropDown from "components/admin/filters/HiddenDropdown";
import {
  reduceFiltersForSearchParams,
  resetBooleanFilters,
  resetFilters,
} from "components/filtersUtils";
import FiltersView from "components/viewerLists/FiltersView";

import AdvancedCaseFilters from "./AdvancedCaseFilters";
import CaseFilters from "./CaseFilters";
import {
  DEFAULT_CASE_LIST_SORT_COLUMN,
  DEFAULT_FINALIZED_CASE_LIST_SORT_COLUMN,
  getDefaultCaseListSortDirection,
} from "../CaseList";

export type FilterObject = { [key: string]: string | string[] };

type CaseFilterFormProps = {
  queue?: QueueEnum;
  features?: FeatureFlags;

  // Pagination props for filtering
  sortBy: { id: string; desc?: boolean }[];
  pageSize: number;
  pageIndex: number;
  setPage: Function;
  setSortBy: Function;

  includeSignedOut: boolean;
  status?: string | null;
  user?: User;
};

const CaseFilterForm = ({
  queue,
  sortBy,
  pageSize,
  pageIndex,
  features,
  setPage,
  includeSignedOut,
  status,
  user,
}: CaseFilterFormProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [initFiltersVisible, setInitFiltersVisible] = useLocalStorage<boolean>(
    "advancedVisible",
    false
  );

  const [caseListQuery, setCaseListQuery] =
    useLocalStorage<FilterObject>("caseListQuery");

  const usp = new URLSearchParams(location.search);
  const initialFilters = useMemo(() => {
    return [...usp.entries()].reduce(
      (acc: FilterObject, [id, value]) => {
        if (value) {
          if (
            (id === "status" || id === "specimensBiopsyType") &&
            typeof value === "string"
          ) {
            // The value is a comma-separated string. Split it into an array.
            // This allows it to work with the multi-select input.
            acc[id] = value.split(",");
          } else {
            acc[id] = Array.isArray(acc[id]) ? value.split(",") : value;
          }
        }
        return acc;
      },
      { biopsyType: [], diagnosisCategory: [] }
    );
  }, [location]);

  const setInitialFilters = (filters: FilterObject) => {
    return Object.keys(filters).reduce((result: FilterObject, key) => {
      result[key] =
        initialFilters[key] || caseListQuery?.[key] || filters[key] || "";
      return result;
    }, {});
  };

  const [basicFilters, setBasicFilters] = useState<FilterObject>(
    setInitialFilters({
      accession: "",
      orderingDoctorName: "",
      patientClinicName: "",
      patientName: "",
      activeAssignmentName: "",
      status: "",
      lab: "",
    })
  );

  const [advancedFilters, setAdvancedFilters] = useState<FilterObject>(
    setInitialFilters({
      receivedDateAfter: "",
      receivedDateBefore: "",
      patientDateOfBirth: "",
      externalAccession: "",
      grossCompletedAfter: "",
      grossCompletedBefore: "",
      accessionCompletedAfter: "",
      accessionCompletedBefore: "",
      slideUploadedAtAfter: "",
      slideUploadedAtBefore: "",
      finalizedAtAfter: "",
      finalizedAtBefore: "",
      diagnosesDiagnosis: "",
      accessionDateFrom: "",
      accessionDateTo: "",
      reportedDateFrom: "",
      reportedDateTo: "",
      specimensBiopsyType: "",
      specimensType: "",
      specimensSite: "",
      specimensProcedure: "",
      specimensClinicalHistory: "",
      biopsyDate: "",
      diagnosisCategory: "",
      diagnosesMicroscopic: "",
      comment: "",
      icd10: "",
    })
  );

  const [additionalFilters, setAdditionalFilters] = useState<{
    [key: string]: boolean | undefined;
  }>({
    allSlidesSuccess: !!initialFilters.allSlidesSuccess,
    hasFlaggedSlides: !!initialFilters.hasFlaggedSlides,
    allOrderedDocumentsUploaded: !!initialFilters.allOrderedDocumentsUploaded,
    hasIncompleteOrders: !!initialFilters.hasIncompleteOrders,
  });

  const prevSearchParams = useRef<{
    queue?: QueueEnum;
    pageIndex?: number;
  }>({});
  const prevFilterCasesQueue = useRef<QueueEnum | undefined>(
    queue || QueueEnum.All
  );
  const firstRender = useRef<boolean>(true);

  const skipFilterBecauseWeAreChagingTheDefaultCaseListQuery = useCallback(
    (urlSearchParams: string) => {
      if (
        urlSearchParams === "" &&
        (basicFilters.orderingDoctorName || basicFilters.activeAssignmentName)
      ) {
        // Hack not to duplicate "sticky user filter" fetch
        setCaseListQuery({
          orderingDoctorName: basicFilters.orderingDoctorName,
          activeAssignmentName: basicFilters.activeAssignmentName,
        });
        setSearchParams(0);
        return true;
      }
      return false;
    },
    [basicFilters]
  );

  useEffect(() => {
    // I guess the function name here is a little tonge-in-cheek
    if (skipFilterBecauseWeAreChagingTheDefaultCaseListQuery(usp.toString()))
      return;
    if (location.pathname !== "/" && usp.get("logout") !== "true") {
      filterCases();
    }
  }, [location.pathname, usp.toString(), pageSize]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else {
      setSearchParams(pageIndex);
    }
  }, [pageIndex, sortBy]);

  const onChangeBasicFilter = (key: string, value: string) => {
    setBasicFilters((basicFilters) => ({ ...basicFilters, [key]: value }));
  };

  const onChangeAdvancedFilter = (key: string, value: string | string[]) => {
    setAdvancedFilters((advancedFilters) => ({
      ...advancedFilters,
      [key]: value,
    }));
  };

  const onChangeAdditionalFilter = (key: string) => {
    setAdditionalFilters((additionalFilters) => ({
      ...additionalFilters,
      [key]: !additionalFilters[key],
    }));
  };

  const onChangeAdvancedDateRangeFilter = (values: { [key: string]: Date }) => {
    onChangeDateRangeFilter(advancedFilters, setAdvancedFilters, values);
  };

  const onChangeDateRangeFilter = (
    filters: { [key: string]: string | string[] },
    set: Function,
    values: { [key: string]: Date }
  ) => {
    const newState = Object.assign({}, filters, values);
    set(newState);
  };

  const checkFiltersChanged = (filters: FilterObject) => {
    return Object.keys(filters).some((s) =>
      Array.isArray(filters[s]) ? filters[s].length > 0 : filters[s] !== ""
    );
  };

  const onHandleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    setPage(0);
    setCaseListQuery({
      orderingDoctorName: basicFilters.orderingDoctorName,
      activeAssignmentName: basicFilters.activeAssignmentName,
    });
    setSearchParams(0);
  };

  const onCheckFiltersChanged = useCallback(() => {
    return (
      checkFiltersChanged(basicFilters) ||
      checkFiltersChanged(advancedFilters) ||
      Object.values(additionalFilters).some((filter) => filter) ||
      pageIndex !== 0
    );
  }, [basicFilters, advancedFilters, sortBy, pageIndex]);

  const onResetFilter = () => {
    const resetBasicFilters = resetFilters(basicFilters);
    const resetAdvancedFilters = resetFilters(advancedFilters);
    setAdditionalFilters(resetBooleanFilters(additionalFilters));
    setBasicFilters(resetBasicFilters);
    setAdvancedFilters(resetAdvancedFilters);
    setPage(0);
    setSearchParams(0, true);
    setCaseListQuery({
      orderingDoctorName: "",
      activeAssignmentName: "",
    });
  };

  const getSortByQuery = (initSortDirection: CaseListDirection) => {
    if (!(sortBy && sortBy.length)) {
      return {};
    }
    const sortDirection = sortBy[0].desc ? "desc" : "asc";
    return {
      ...(sortDirection !== initSortDirection
        ? {
            sortDirection: sortDirection,
          }
        : {}),
      ...(sortBy[0].id !== DEFAULT_FINALIZED_CASE_LIST_SORT_COLUMN &&
      sortBy[0].id !== DEFAULT_CASE_LIST_SORT_COLUMN
        ? {
            sortColumn: sortBy[0].id,
          }
        : {}),
    };
  };

  const setSearchParams = useCallback(
    (pageIndex: number, isReset?: boolean) => {
      const advFilters = isReset
        ? resetFilters(advancedFilters)
        : advancedFilters;
      const filters = {
        ...(isReset ? resetFilters(basicFilters) : basicFilters),
        ...Object.keys(additionalFilters).reduce(
          (acc: FilterObject, key: string) => {
            if (additionalFilters[key] && queue === QueueEnum.Working) {
              acc[key] = "true";
            }
            return acc;
          },
          {}
        ),
        ...Object.keys(advFilters).reduce((acc: FilterObject, key: string) => {
          if (advFilters[key]) {
            acc[key] = Array.isArray(advFilters[key])
              ? (advFilters[key] as string[]).join(",")
              : advFilters[key];
          }
          return acc;
        }, {}),
      };

      const params: any = {
        ...reduceFiltersForSearchParams(filters),
        ...(pageIndex !== 0 && { page: pageIndex + 1 }),
        ...getSortByQuery(getDefaultCaseListSortDirection(queue)),
      };

      prevSearchParams.current = {
        queue: queue,
        pageIndex: pageIndex,
      };

      navigate(
        `${location.pathname}?${new URLSearchParams(params).toString()}`,
        {
          replace: true,
        }
      );
    },
    [
      basicFilters,
      advancedFilters,
      additionalFilters,
      sortBy,
      pageIndex,
      location,
      queue,
      usp,
    ]
  );

  const filterCases = useCallback(() => {
    const filters = {
      ...basicFilters,
      ...advancedFilters,
    };
    const defaultSort =
      status === "finalized"
        ? DEFAULT_FINALIZED_CASE_LIST_SORT_COLUMN
        : DEFAULT_CASE_LIST_SORT_COLUMN;

    const apiState = {
      ...Object.keys(filters).reduce((acc: FilterObject, key: string) => {
        if (filters[key] && filters[key].length) {
          acc[key] = filters[key];
        }
        return acc;
      }, {}),
      ...Object.keys(additionalFilters).reduce(
        (acc: { [key: string]: boolean }, key: string) => {
          if (additionalFilters[key] && queue === QueueEnum.Working) {
            acc[key] = true;
          }
          return acc;
        },
        {}
      ),
      page: pageIndex + 1,
      paginationPerPage: pageSize,
      sortColumn:
        sortBy.length &&
        sortBy[0].id &&
        sortBy[0].id !== DEFAULT_CASE_LIST_SORT_COLUMN &&
        sortBy[0].id !== DEFAULT_FINALIZED_CASE_LIST_SORT_COLUMN
          ? sortBy[0].id
          : defaultSort,
      sortDirection:
        (prevFilterCasesQueue.current === queue || usp.get("sortDirection")) &&
        sortBy.length
          ? sortBy[0].desc
            ? CaseListDirection.Desc
            : CaseListDirection.Asc
          : getDefaultCaseListSortDirection(queue),
      status:
        // Default the screened and screening queues to active and working statuses
        queue === QueueEnum.Screened || queue === QueueEnum.Screen
          ? ["active", "working"]
          : queue === QueueEnum.Finalized
          ? ["lis-pending", "finalized"]
          : status || filters.status || null,
      includeSignedOut: includeSignedOut,
      ...(queue === QueueEnum.Preliminary && { hasActivePrelimReport: true }),
      queue:
        // if there is no queue or the queue is screened or screening, default to All
        queue
          ? queue === QueueEnum.Screen || queue === QueueEnum.Screened
            ? QueueEnum.All
            : queue
          : userDefaultQueue(user),
      ...(queue === QueueEnum.Screen && { isScreening: true }),
      ...(queue === QueueEnum.Screened && { isRequestScreenComplete: true }),
    };
    prevFilterCasesQueue.current = queue;
    dispatch(fetchCases(apiState));
  }, [
    basicFilters,
    advancedFilters,
    additionalFilters,
    sortBy,
    pageIndex,
    pageSize,
    queue,
    includeSignedOut,
    status,
    usp,
  ]);

  return (
    <FiltersView
      onSubmit={onHandleSubmit}
      submitLabel="Search Cases"
      onReset={onResetFilter}
      onCheckFiltersChanged={onCheckFiltersChanged}
    >
      <>
        <div className="filter__section">
          <CaseFilters
            {...(basicFilters as any)}
            onChange={onChangeBasicFilter}
            features={features}
            user={user}
            queue={queue}
          />
        </div>
        <HiddenDropDown
          label="Advanced Filters"
          type="filter"
          setLocalStorage={setInitFiltersVisible}
          isInitOpen={initFiltersVisible}
        >
          <div className="filter__section">
            <AdvancedCaseFilters
              {...(advancedFilters as any)}
              allSlidesSuccess={additionalFilters.allSlidesSuccess}
              hasFlaggedSlides={additionalFilters.hasFlaggedSlides}
              hasIncompleteOrders={additionalFilters.hasIncompleteOrders}
              allOrderedDocumentsUploaded={
                additionalFilters.allOrderedDocumentsUploaded
              }
              onChange={onChangeAdvancedFilter}
              onChangeDateRange={onChangeAdvancedDateRangeFilter}
              onChangeCheckbox={onChangeAdditionalFilter}
              features={features}
              queue={queue}
            />
          </div>
        </HiddenDropDown>
      </>
    </FiltersView>
  );
};

export default CaseFilterForm;
